import { setCookie, getCookie} from './cookie.js';
import { extractPkFromLocation } from '../../scripts/helpers/extractPkFromLocation.js';
import { extractDistributionOutletFromLocation } from '../../scripts/helpers/extractDistributionOutletFromLocation.js';
import { checkPkInSessionStorage, getPkFromSessionStorage, setPkInSessionStorage } from '../../scripts/helpers/handlePkInSessionStorage.js';
import { checkDistributionOutletInSessionStorage, getDistributionOutletFromSessionStorage, setDistributionOutletInSessionStorage } from '../../scripts/helpers/handleDistributionOutletInSessionStorage.js';

class Calculators {
  #calculatorsContainer;
  constructor(calculatorsContainer) {
    this.#calculatorsContainer = calculatorsContainer;
  }

  /**
   * Initializes component.
   * init()
   * @param {location} locationObj - should be structured like a window location (https://www.w3schools.com/jsref/obj_location.asp).
   * This parameter must not be set in production environments. Only for testing relevant.
   * @param {function} getCookieFn - should replace getCookie in UnitTest environment. When we can fix the "require not found"-error
   * when using jest.mock we could get rid of this workaround for testing
   */
  init(locationObj = location, getCookieFn = getCookie) {
    const URL_CONFIG = {
      DE: {
        domain: 'easycredit.de',
        pknr: '1.de.4007.1'
      },
      AT: {
        domain: 'derfairecredit.at',
        pknr: '3.at.100.1'
      }
    };

    function adoptNewUrlToEnvironment(environmentId, country) {
      if (environmentId === 'prod') {
        return `https://angebot.${URL_CONFIG[country].domain}/strapp/einstieg`;
      }
      if (environmentId === 'local') {
        environmentId = 'integ';
      }
      return `https://angebot.${environmentId}.${URL_CONFIG[country].domain}/strapp/einstieg`;
    }

    function setInitialFormLink(webcomponent, locationObject) {
      const country = window.tb.PROVIDED_COUNTRY;
      const env_id = window.tb.WP_ENV;
      let pk = URL_CONFIG[country].pknr;
      const pkFromLocationObject = extractPkFromLocation(locationObject);
      const distributionOutletFromLocationObject = extractDistributionOutletFromLocation(location);
      const useSessionStorageForPkAndDistributionOutlet = webcomponent.getAttribute('save-pk-in-session-storage');

      if (useSessionStorageForPkAndDistributionOutlet === 'true') {
        if (!checkPkInSessionStorage()) {
          if (pkFromLocationObject !== '') {
            pk = pkFromLocationObject;
          } else {
            if (distributionOutletFromLocationObject !== '') {
              pk = distributionOutletFromLocationObject;
            } else {
              pk = URL_CONFIG[country].pknr;
            }
          }
        } else {
          if (pkFromLocationObject !== '' && getPkFromSessionStorage() !== pkFromLocationObject) {
            pk = pkFromLocationObject;
          } else if (checkDistributionOutletInSessionStorage()) {
            if (distributionOutletFromLocationObject !== '' && getDistributionOutletFromSessionStorage() !== distributionOutletFromLocationObject) {
              pk = distributionOutletFromLocationObject;
            } else {
              pk = getPkFromSessionStorage();
            }
          } else {
            pk = getPkFromSessionStorage();
          }
        }

        setPkInSessionStorage(pk);
        setDistributionOutletInSessionStorage(pk);
      }

      if (webcomponent.getAttribute('use-a-tag-as-cta') === null) {
        webcomponent.removeAttribute('form-link');
        webcomponent.setAttribute('form-link', adoptNewUrlToEnvironment(env_id, country));
      } else {
        webcomponent.setAttribute('form-link', webcomponent.getAttribute('form-link').replace(/([?&]pk=)([^&]*)/, `$1${pk}`).replace(/([?&]vertriebsstelle=)([^&]*)/, `$1${pk}`));
      }

      webcomponent.setAttribute('cta-inactive-default', 'false');
    }

    this.#calculatorsContainer.addEventListener('kreditrechner_jetzt_berechnen', (e) => {
      let usingATagAsCta = false;

      if (e.detail) {
        if (e.detail.length >= 1) {
          if (e.detail[0].handledViaCta) {
            usingATagAsCta = true;
          }
        }
      }

      const kreditRechnerElem = e.currentTarget.querySelector('[datalayer-eventname]');

      if (kreditRechnerElem !== null) {
        const dataLayerEventname = kreditRechnerElem.getAttribute('datalayer-eventname');

        if (dataLayerEventname !== null) {
          const dataLayerPage = window.location.href.replace(window.location.origin, '');

          const pushData = {
            'event': dataLayerEventname,
            'page': dataLayerPage,
            'value': getCookieFn('antrag.kreditWunsch'),
            'duration': getCookieFn('antrag.laufzeit'),
            'rate': getCookieFn('antrag.rate')
          };

          if (typeof dataLayer !== 'undefined') {
            dataLayer.push(pushData);
          }
        }
      }

      if (!usingATagAsCta) {
        const env_id = window.tb.WP_ENV;
        const country = window.tb.PROVIDED_COUNTRY;
        const pkNr = checkPkInSessionStorage() ? (extractPkFromLocation(locationObj) !== '' && getPkFromSessionStorage() !== extractPkFromLocation(locationObj) ? extractPkFromLocation(locationObj) : getPkFromSessionStorage()) : (extractPkFromLocation(locationObj) === '' ? URL_CONFIG[country].pknr : extractPkFromLocation(locationObj));

        if (!checkPkInSessionStorage()) {
          setPkInSessionStorage(pkNr);
        }

        let urlParams = {
          wunschbetrag: getCookieFn('antrag.kreditWunsch'),
          pk: pkNr,
          vertriebsstelle: pkNr,
        };

        if (e.detail) {
          if (e.detail.length > 0) {
            const filteredDetailKeys = Object.keys(e.detail[0]).filter(key => key !== 'handledViaCta' && key !== 'wunschbetrag' && key !== 'pk' && key !== 'vertriebsstelle');

            filteredDetailKeys.forEach(key => {
              urlParams[key] = e.detail[0][key];
            });
          }
        }

        const parameterString = prepareCreditUrlParams(urlParams);
        window.open(adoptNewUrlToEnvironment(env_id, country) + parameterString);
      }
    });

    let component = document.querySelector('kredit-rechner');

    if (component) {
      setInitialFormLink(component, locationObj);
    } else {
      let intvalCounter = 0;

      const getComponentIntval = setInterval(() => {
        component = document.querySelector('kredit-rechner');

        if (component || intvalCounter > 100) {
          setInitialFormLink(component, locationObj);
          clearInterval(getComponentIntval);
        }

        intvalCounter++;
      }, 100);
    }
  }
}

function prepareCreditUrlParams(params) {
  const paramStrings = Object.keys(params)
    .map((paramName) => {
      return `${paramName}=${params[paramName]}`
    });
  if (paramStrings.length) {
    return '?' + [].concat(paramStrings).join('&');
  }

  return '';
}

const calculatorElements = document.querySelectorAll('[data-js-block="calculators"]');
let horseShoeImported = false;

if (calculatorElements.length > 0) {
  calculatorElements.forEach(calculatorElement => {
    const calculatorClass = new Calculators(calculatorElement);
    calculatorClass.init();
  });
}

export default Calculators;
